import { useEffect, useRef, useState } from 'react';
import { Check, Pause, Play, Scissors, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TrechoVideo {
  inicio: number;
  fim: number;
}

const formatar = (s: number) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const seg = Math.floor(s % 60);
  const dec = Math.floor((s % 1) * 10);
  return `${m}:${String(seg).padStart(2, '0')}.${dec}`;
};

// Escolhe o trecho do vídeo antes de mandar. O corte em si é feito no servidor
// (ffmpeg), que também converte para MP4: o navegador só marca início e fim.
export function EditorVideo({
  arquivo,
  trechoInicial,
  onCancelar,
  onConcluir,
}: {
  arquivo: File;
  trechoInicial?: TrechoVideo | null;
  onCancelar: () => void;
  onConcluir: (trecho: TrechoVideo | null) => void;
}) {
  // URL criada e liberada no mesmo efeito: com useMemo + cleanup, o modo
  // estrito do React liberava a URL e o vídeo das miniaturas não carregava.
  const [url, setUrl] = useState('');
  useEffect(() => {
    const u = URL.createObjectURL(arquivo);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [arquivo]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const trilhaRef = useRef<HTMLDivElement>(null);
  const [duracao, setDuracao] = useState(0);
  const [inicio, setInicio] = useState(trechoInicial?.inicio ?? 0);
  const [fim, setFim] = useState(trechoInicial?.fim ?? 0);
  const [atual, setAtual] = useState(0);
  const [tocando, setTocando] = useState(false);
  const [arrastando, setArrastando] = useState<'inicio' | 'fim' | null>(null);
  const [miniaturas, setMiniaturas] = useState<string[]>([]);

  // Quadros da trilha: 8 miniaturas tiradas do próprio vídeo.
  useEffect(() => {
    if (!duracao || !url) return;
    let cancelado = false;
    const v = document.createElement('video');
    v.src = url;
    v.muted = true;
    v.preload = 'auto';
    const c = document.createElement('canvas');
    const quadros: string[] = [];
    const tirar = async () => {
      for (let i = 0; i < 8 && !cancelado; i += 1) {
        await new Promise<void>((resolve) => {
          v.onseeked = () => resolve();
          v.currentTime = Math.min(duracao - 0.05, (duracao / 8) * i + 0.05);
        });
        c.width = 96;
        c.height = Math.round((96 * v.videoHeight) / Math.max(1, v.videoWidth)) || 54;
        c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
        quadros.push(c.toDataURL('image/jpeg', 0.6));
      }
      if (!cancelado) setMiniaturas(quadros);
    };
    v.onloadeddata = () => void tirar();
    return () => {
      cancelado = true;
    };
  }, [duracao, url]);

  // Tocando: fica em loop dentro do trecho escolhido.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setAtual(v.currentTime);
      if (fim > 0 && v.currentTime >= fim) {
        v.currentTime = inicio;
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [inicio, fim]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancelar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancelar]);

  const segundosDoPonteiro = (clientX: number) => {
    const r = trilhaRef.current!.getBoundingClientRect();
    return Math.max(0, Math.min(duracao, ((clientX - r.left) / r.width) * duracao));
  };

  useEffect(() => {
    if (!arrastando) return;
    const mover = (e: PointerEvent) => {
      const s = segundosDoPonteiro(e.clientX);
      if (arrastando === 'inicio') setInicio(Math.min(s, fim - 0.5));
      else setFim(Math.max(s, inicio + 0.5));
      if (videoRef.current) videoRef.current.currentTime = s;
    };
    const soltar = () => setArrastando(null);
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
    // segundosDoPonteiro só lê refs e a duração.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrastando, inicio, fim, duracao]);

  const alternar = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.currentTime < inicio || v.currentTime >= fim) v.currentTime = inicio;
      void v.play();
      setTocando(true);
    } else {
      v.pause();
      setTocando(false);
    }
  };

  const pct = (s: number) => (duracao ? (s / duracao) * 100 : 0);
  const cortou = duracao > 0 && (inicio > 0.05 || fim < duracao - 0.05);
  const tamanhoTrecho = Math.max(0, fim - inicio);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cortar vídeo"
      className="fixed inset-0 z-[150] flex flex-col bg-black"
    >
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <button
          type="button"
          onClick={onCancelar}
          aria-label="Cancelar"
          className="flex size-9 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white"
        >
          <X size={20} />
        </button>
        <p className="mr-auto flex items-center gap-2 text-sm font-semibold text-white">
          <Scissors size={16} /> Cortar vídeo
        </p>
        <button
          type="button"
          onClick={() => onConcluir(cortou ? { inicio, fim } : null)}
          disabled={!duracao}
          className="flex items-center gap-1.5 rounded-lg bg-tertiary px-4 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          <Check size={15} /> {cortou ? 'Usar este trecho' : 'Usar vídeo inteiro'}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center p-4">
        <video
          ref={videoRef}
          src={url || undefined}
          playsInline
          onClick={alternar}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (!Number.isFinite(d)) return;
            setDuracao(d);
            setFim((f) => (f > 0 ? Math.min(f, d) : d));
          }}
          onPause={() => setTocando(false)}
          onPlay={() => setTocando(true)}
          className="max-h-full max-w-full cursor-pointer rounded-lg"
        />
      </div>

      <div className="space-y-3 border-t border-white/10 px-4 py-4">
        <div className="flex items-center justify-between text-xs tabular-nums text-white/70">
          <button
            type="button"
            onClick={alternar}
            className="flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 font-semibold text-white hover:bg-white/20"
          >
            {tocando ? <Pause size={14} /> : <Play size={14} />} {tocando ? 'Pausar' : 'Ver trecho'}
          </button>
          <span>
            {formatar(inicio)} até {formatar(fim)} ·{' '}
            <strong className="text-white">{formatar(tamanhoTrecho)}</strong> de {formatar(duracao)}
          </span>
        </div>

        {/* Trilha com miniaturas, área escolhida e duas alças. */}
        <div
          ref={trilhaRef}
          className="relative mx-auto h-14 w-full max-w-4xl select-none overflow-hidden rounded-lg bg-white/5"
        >
          <div className="absolute inset-0 flex">
            {miniaturas.map((src, i) => (
              <img key={i} src={src} alt="" className="h-full flex-1 object-cover opacity-80" />
            ))}
          </div>
          <div
            className="absolute inset-y-0 left-0 bg-black/70"
            style={{ width: `${pct(inicio)}%` }}
          />
          <div
            className="absolute inset-y-0 right-0 bg-black/70"
            style={{ width: `${100 - pct(fim)}%` }}
          />
          <div
            className="absolute inset-y-0 border-y-2 border-tertiary"
            style={{ left: `${pct(inicio)}%`, width: `${pct(fim) - pct(inicio)}%` }}
          />
          <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${pct(atual)}%` }} />
          {(['inicio', 'fim'] as const).map((alca) => (
            <button
              key={alca}
              type="button"
              aria-label={alca === 'inicio' ? 'Início do trecho' : 'Fim do trecho'}
              onPointerDown={(e) => {
                e.preventDefault();
                setArrastando(alca);
              }}
              onKeyDown={(e) => {
                const passo = e.shiftKey ? 1 : 0.1;
                if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                e.preventDefault();
                const d = e.key === 'ArrowLeft' ? -passo : passo;
                if (alca === 'inicio') setInicio((s) => Math.max(0, Math.min(fim - 0.5, s + d)));
                else setFim((s) => Math.min(duracao, Math.max(inicio + 0.5, s + d)));
              }}
              className={cn(
                'absolute inset-y-0 flex w-4 -translate-x-1/2 cursor-ew-resize items-center justify-center rounded-md bg-tertiary focus:outline-none focus-visible:ring-2 focus-visible:ring-white'
              )}
              style={{ left: `${pct(alca === 'inicio' ? inicio : fim)}%` }}
            >
              <span className="h-5 w-0.5 rounded bg-white/90" />
            </button>
          ))}
        </div>
        <p className="text-center text-[11px] text-white/50">
          Arraste as alças verdes. O vídeo sai em MP4, que é o formato que o WhatsApp aceita.
        </p>
      </div>
    </div>
  );
}
